import React, { useEffect, useRef } from 'react';
import { SourceItem } from '../../shared/models/source-item.model';
import { ArrayProvider } from '../../state/ArrayContext';
import { useCreateInternalStateBranch, useInternalAction, InternalActionName } from '../../state/InternalContext';
import { findFirstComponentOfType } from '../internal/TextExtractor';
import { useMemo, useState } from 'react';
import _ from 'lodash';

interface StoreArrayProps {
    store: string,
    content: Array<Node>,
    children: React.ReactNode,
}

/***
 * A component that stores an array of items in the shared state.
 * The specification is used to load the array of items. It must be an ArraySource specification.
 * This is a utility component that can be used anywhere and affects the shared state for all its
 * descendant content.
 */
export function StoreArray({ store, content: _content, children }: StoreArrayProps) {
    const createInternalStateBranch = useCreateInternalStateBranch();

    // Get the from array from the from child
    const fromChild = useMemo(() => findFirstComponentOfType(children, "arg-from"), [children]);
    const otherChildren = useMemo(() => React.Children.toArray(children).filter(child => !React.isValidElement(child) || child.type !== "arg-from"), [children]);

    const [array, setArray] = useState<Array<SourceItem>>([]);

    const BranchComponent = useMemo(() => createInternalStateBranch({}), [createInternalStateBranch]);

    // Use a ref to track the current array value
    const arrayRef = useRef<Array<SourceItem>>(array);
    arrayRef.current = array;

    // Create a wrapper component that updates the array in the branch state
    const ArrayUpdatingBranchComponent = useMemo(() => {
        return ({ children }: { children: React.ReactNode }) => {
            return (
                <BranchComponent>
                    <ArrayUpdater store={store} arrayRef={arrayRef}>
                        {children}
                    </ArrayUpdater>
                </BranchComponent>
            );
        };
    }, [BranchComponent, store]);

    return <>
        <ArrayUpdatingBranchComponent>
            {otherChildren}
        </ArrayUpdatingBranchComponent>
        <ArrayProvider setArray={setArray}>
            {fromChild?.props.children}
        </ArrayProvider>
    </>
}

// Component that runs inside the branch context to update the array
function ArrayUpdater({ store, arrayRef, children }: { store: string, arrayRef: React.MutableRefObject<Array<SourceItem>>, children: React.ReactNode }) {
    const setStoredArray = useInternalAction(InternalActionName.SET_STORED_ARRAY);
    const prevArrayRef = useRef<Array<SourceItem>>(arrayRef.current);

    // Update the stored array whenever the local array changes
    useEffect(() => {
        const currentArray = arrayRef.current;
        // Only update if the array content has actually changed (deep comparison)
        if (!_.isEqual(prevArrayRef.current, currentArray)) {
            setStoredArray({ [store]: currentArray || [] });
            prevArrayRef.current = currentArray;
        }
    });

    return <>{children}</>;
}